import { createSelector } from "reselect";
import _intersection from "lodash/intersection";
import _difference from "lodash/fp/difference";
import { RootState as RS } from "../types";
import { SliceState } from "./reducer";
import { doArraysHaveCommonElement, isArraySubset } from "../../utils/array";
import { X2yController } from "./types";

const getX2ySlice = (s: RS): SliceState => s.x2y;

// Slice for source table

export const createGetX2yState = (controller: X2yController) => (s: RS) =>
  getX2ySlice(s)[controller];

// == IS FETCHING STUFF ==

export const createGetFetchingIds = (controller: X2yController) => {
  const getX2yState = createGetX2yState(controller);
  return (s: RS) => getX2yState(s)?.fetchingIds;
};

// for source table and x ids (true all are already fetching)
export const createGetIsFetching = (controller: X2yController) => {
  const getFetchingIds = createGetFetchingIds(controller);

  return (s: RS, xIds: number[]) => {
    const fetchingIds = getFetchingIds(s);
    if (!fetchingIds) return null;

    return isArraySubset(fetchingIds, xIds);
  };
};

export const createGetNonFetchingIdSubset = (controller: X2yController) => {
  const getFetchingIds = createGetFetchingIds(controller);

  return (s: RS, xIds: number[]) => {
    const fetchingIds = getFetchingIds(s);
    return _difference(xIds, fetchingIds);
  };
};

// == IS SUBMITTING STUFF ==

export const createGetSubmittingIds = (controller: X2yController) => {
  const getX2yState = createGetX2yState(controller);
  return (s: RS) => getX2yState(s)?.submittingIds;
};

// isSubmitting for source table and x ids (true if at least one submitting)
export const creteGetIsSubmitting = (controller: X2yController) => {
  const getImplementationState = createGetX2yState(controller);

  return (s: RS, xIds: number[]) => {
    const fetchingIds = getImplementationState(s)?.fetchingIds;
    if (!fetchingIds) return false;

    return doArraysHaveCommonElement(fetchingIds, xIds);
  };
};

// Data for X2y component
const passXIds = (s: RS, xIds: number[]) => xIds;

export const createGetX2yData = (controller: X2yController) => {
  const getImplementationState = createGetX2yState(controller);
  const getFetchingIds = createGetFetchingIds(controller);
  const getIsAtLeastOneIdSubmitting = creteGetIsSubmitting(controller);

  return createSelector(
    getImplementationState,
    passXIds,
    getFetchingIds,
    getIsAtLeastOneIdSubmitting,
    (x2yState, xIds, fetchingIds, isAtLeastOneSubmitting) => {
      const { ysById, selectionListsByXId } = x2yState;
      const allDataLoaded = xIds.every((id) => !!selectionListsByXId[id]);

      const idsSelectedForAllXs = _intersection(
        ...xIds.map((xId) => selectionListsByXId[xId]?.selected?.map(Number))
      );

      const idsUnselectedForAllXs = _intersection(
        ...xIds.map((xId) => selectionListsByXId[xId]?.unselected?.map(Number))
      );

      const isAtLeastOneFetching = xIds.some((id) => fetchingIds?.includes(id));

      return {
        isReady: allDataLoaded,
        ysById,
        selected: idsSelectedForAllXs,
        unselected: idsUnselectedForAllXs,
        isFetching: isAtLeastOneFetching,
        isSubmitting: isAtLeastOneSubmitting,
      };
    }
  );
};
